// components/Atoms/Icon/Icon.js
import React from "react";
import PropTypes from "prop-types";

const LIBS_CACHE = {};

async function getLib(prefix) {
  if (LIBS_CACHE[prefix]) return LIBS_CACHE[prefix];
  let lib = null;
  switch (prefix) {
    case "Ai":  lib = await import("react-icons/ai"); break;
    case "Bi":  lib = await import("react-icons/bi"); break;
    case "Bs":  lib = await import("react-icons/bs"); break;
    case "Cg":  lib = await import("react-icons/cg"); break;
    case "Ci":  lib = await import("react-icons/ci"); break;
    case "Di":  lib = await import("react-icons/di"); break;
    case "Fc":  lib = await import("react-icons/fc"); break;
    case "Fa":  lib = await import("react-icons/fa"); break;
    case "Fa6": lib = await import("react-icons/fa6"); break;
    case "Fi":  lib = await import("react-icons/fi"); break;
    case "Gi":  lib = await import("react-icons/gi"); break;
    case "Go":  lib = await import("react-icons/go"); break;
    case "Gr":  lib = await import("react-icons/gr"); break;
    case "Hi":  lib = await import("react-icons/hi"); break;
    case "Hi2": lib = await import("react-icons/hi2"); break;
    case "Im":  lib = await import("react-icons/im"); break;
    case "Io":  lib = await import("react-icons/io"); break;
    case "Io5": lib = await import("react-icons/io5"); break;
    case "Lia": lib = await import("react-icons/lia"); break;
    case "Lu":  lib = await import("react-icons/lu"); break;
    case "Md":  lib = await import("react-icons/md"); break;
    case "Pi":  lib = await import("react-icons/pi"); break;
    case "Ri":  lib = await import("react-icons/ri"); break;
    case "Rx":  lib = await import("react-icons/rx"); break;
    case "Si":  lib = await import("react-icons/si"); break;
    case "Sl":  lib = await import("react-icons/sl"); break;
    case "Tb":  lib = await import("react-icons/tb"); break;
    case "Tfi": lib = await import("react-icons/tfi"); break;
    case "Ti":  lib = await import("react-icons/ti"); break;
    case "Vsc": lib = await import("react-icons/vsc"); break;
    case "Wi":  lib = await import("react-icons/wi"); break;
    case "Ph":  lib = await import("@phosphor-icons/react"); break;
  }
  if (lib) LIBS_CACHE[prefix] = lib;
  return lib;
}

/**
 * Prefix detection for dynamic "name" like "FaBeer", "PhConfetti", etc.
 * Put longer prefixes first.
 */
const PREFIXES = [
  "Fa6",
  "Hi2",
  "Io5",
  "Vsc",
  "Tfi",
  "Lia",
  "Ph", // ✅ new (put early)
  "Fa",
  "Hi",
  "Md",
  "Ai",
  "Tb",
  "Gi",
  "Io",
  "Bi",
  "Bs",
  "Cg",
  "Ci",
  "Di",
  "Fc",
  "Fi",
  "Go",
  "Gr",
  "Im",
  "Lu",
  "Pi",
  "Ri",
  "Rx",
  "Si",
  "Sl",
  "Ti",
  "Wi",
];

const USAGE_PRESETS = {
  inline: { size: "sm" },
  decorative: { size: "md" },
  feature: { size: "xl" },
  bullet: { size: "md" }, // ✅ recommended preset
};

const SIZE_CLASS = {
  xs: "icon-xs",
  sm: "icon-sm",
  md: "icon-md",
  lg: "icon-lg",
  xl: "icon-xl",
  "2xl": "icon-2xl",
};

function getPrefixFromName(name) {
  if (typeof name !== "string" || name.length < 2) return null;
  return PREFIXES.find((p) => name.startsWith(p)) || null;
}

const Icon = ({
  name,
  library,
  usage,
  size = "md",
  className = "",
  title,
  decorative = true,
  ...props
}) => {
  const [IconComponent, setIconComponent] = React.useState(null);

  React.useEffect(() => {
    if (!name) return;
    const prefix = library || getPrefixFromName(name);
    if (!prefix) return;
    getLib(prefix).then((lib) => {
      if (lib && lib[name]) setIconComponent(() => lib[name]);
    });
  }, [name, library]);

  const preset = usage && USAGE_PRESETS[usage] ? USAGE_PRESETS[usage] : null;
  const finalSize = preset?.size || size;

  if (!IconComponent) return null;

  const sizeClass = SIZE_CLASS[finalSize] || SIZE_CLASS.md;
  const iconClasses = `${sizeClass} ${className}`.trim();

  // ✅ Phosphor: allow weight prop; default to duotone for bullet, regular otherwise (unless user overrides)
  const isPhosphor = typeof name === "string" && name.startsWith("Ph");
  const phosphorWeight =
    props.weight || (usage === "bullet" ? "duotone" : "regular");

  // Accessibility: default decorative icons to aria-hidden
  const ariaHidden = decorative ? true : undefined;
  const ariaLabel = decorative ? undefined : title || name;

  return (
    <IconComponent
      className={iconClasses}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      title={decorative ? undefined : title}
      {...(isPhosphor ? { weight: phosphorWeight } : {})}
      {...props}
    />
  );
};

Icon.propTypes = {
  /** e.g. "FaBeer", "PhConfetti" */
  name: PropTypes.string.isRequired,

  /**
   * Optional explicit library override (rare).
   * For Phosphor you can pass library="Ph" and name="PhConfetti"
   * but normally prefix detection is enough.
   */
  library: PropTypes.oneOf(PREFIXES),

  /** semantic preset */
  usage: PropTypes.oneOf(Object.keys(USAGE_PRESETS)),

  /** size token (maps to icon-* classes) */
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "2xl"]),

  /** extra classes */
  className: PropTypes.string,

  /** a11y title if not decorative */
  title: PropTypes.string,

  /** if true, icon is aria-hidden */
  decorative: PropTypes.bool,
};

export default Icon;